import React, { useEffect, useRef, useState } from 'react';
import { fileToBase64 } from '../../components/shared';
import { anexarDocumentoEmpleado } from './apiExpediente';
import { nombreFoto, quitarPrefijoDataUrl } from './procesarDocumento';
import { useEvidencia, InfoEvidencia, useGuardiaSalida, DialogoSalida } from './evidencia';

// Carga documental Mobile (M.2B.2): "Tomar foto" y "Subir archivo" hacia el
// endpoint existente api/empleado/anexar. Nada se sube sin confirmación, el
// éxito sólo se acepta con success:true y nunca se reintenta automáticamente
// (el backend no es idempotente). El archivo pendiente vive sólo en memoria
// mientras esta vista siga abierta. Selección, cámara, vista previa y guardia
// de salida vienen de evidencia.jsx (compartido con Registro de capacitación).
//
// Fases: inactivo -> revision (foto) -> confirmar -> subiendo
//        -> éxito (vuelve a inactivo) | verificando -> error | dudoso

export function CargaDocumento({ idEmpleado, nombreEmpleado, anexos, onRefrescar, guardiaSalida }) {
  const [fase, setFase] = useState('inactivo');
  const [aviso, setAviso] = useState(null);
  const montado = useRef(true);
  const verificacion = useRef(null); // { vioCarga: bool } mientras se refresca tras un error

  const ev = useEvidencia({
    nombreFoto: () => nombreFoto(idEmpleado),
    alSeleccionar: e => { setAviso(null); setFase(e.origen === 'foto' ? 'revision' : 'confirmar'); },
    alError: texto => { setFase('inactivo'); setAviso({ tono: 'error', texto }); }
  });
  const carga = ev.evidencia;
  const subiendo = fase === 'subiendo';
  const [salidaPendiente, setSalidaPendiente] = useGuardiaSalida(guardiaSalida, carga != null);

  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  const descartar = () => {
    ev.descartar();
    setFase('inactivo');
    verificacion.current = null;
  };

  const subir = async () => {
    if (!carga || subiendo) return;
    setAviso(null);
    setFase('subiendo');
    try {
      const base64 = quitarPrefijoDataUrl(await fileToBase64(carga.blob));
      await anexarDocumentoEmpleado({ id_empleado: String(idEmpleado), base64, nombre_archivo: carga.nombre });
      if (!montado.current) return;
      descartar();
      setAviso({ tono: 'exito', texto: 'Documento agregado' });
      onRefrescar();
    } catch {
      if (!montado.current) return;
      // Respuesta ambigua o fallida: conservar el archivo y revisar la lista
      // real antes de permitir un nuevo intento manual.
      verificacion.current = { vioCarga: false };
      setFase('verificando');
      onRefrescar();
    }
  };

  // Tras un fallo: esperar a que la lista de anexos se vuelva a cargar.
  useEffect(() => {
    const v = verificacion.current;
    if (fase !== 'verificando' || !v) return;
    if (anexos.estado === 'cargando') { v.vioCarga = true; return; }
    if (!v.vioCarga) return;
    verificacion.current = null;
    const nombres = anexos.estado === 'listo' ? (anexos.datos || []).map(a => a.nombre) : [];
    if (carga && carga.origen === 'foto' && nombres.includes(carga.nombre)) {
      setFase('dudoso');
      setAviso({ tono: 'info', texto: `No se pudo confirmar la carga, pero la foto ${carga.nombre} ya aparece en Documentos firmados. Revisa la lista antes de volver a subirla.` });
    } else {
      setFase('error');
      setAviso({
        tono: 'error',
        texto: carga && carga.origen === 'archivo'
          ? 'No se pudo confirmar la carga. Revisa la lista de documentos antes de volver a subir el archivo.'
          : 'No se pudo confirmar la carga.'
      });
    }
  }, [fase, anexos, carga]);

  const mostrarPanel = carga != null || ev.procesando;
  const accionesBloqueadas = mostrarPanel;
  const abrir = abridor => { setAviso(null); abridor(); };

  return (
    <div className="m-carga">
      <div className="m-carga-acciones">
        <button type="button" className="m-carga-accion" onClick={() => abrir(ev.abrirCamara)} disabled={accionesBloqueadas}>
          <i className="ti ti-camera" aria-hidden="true"></i> Tomar foto
        </button>
        <button type="button" className="m-carga-accion" onClick={() => abrir(ev.abrirArchivo)} disabled={accionesBloqueadas}>
          <i className="ti ti-upload" aria-hidden="true"></i> Subir archivo
        </button>
        {ev.entradas}
      </div>

      {aviso && (
        <p className={`m-carga-aviso m-carga-aviso-${aviso.tono}`} role={aviso.tono === 'error' ? 'alert' : 'status'}>
          <i className={`ti ${aviso.tono === 'exito' ? 'ti-circle-check' : aviso.tono === 'info' ? 'ti-info-circle' : 'ti-alert-triangle'}`} aria-hidden="true"></i>
          <span>{aviso.texto}</span>
        </p>
      )}

      {mostrarPanel && (
        <section className="m-carga-panel" aria-label="Documento por subir" aria-busy={ev.procesando || subiendo || fase === 'verificando'}>
          {ev.procesando || !carga ? (
            <p className="m-carga-estado" role="status"><i className="ti ti-loader-2 m-girando" aria-hidden="true"></i> Preparando foto…</p>
          ) : (
            <>
              <InfoEvidencia evidencia={carga} />

              {fase === 'revision' && (
                <div className="m-carga-botones">
                  <button type="button" className="m-btn m-btn-secundario" onClick={ev.abrirCamara}>Repetir</button>
                  <button type="button" className="m-btn m-btn-primario" onClick={() => setFase('confirmar')}>Usar foto</button>
                </div>
              )}

              {fase === 'confirmar' && (
                <>
                  <p className="m-carga-destino">Se agregará a <strong>Documentos firmados</strong> de {nombreEmpleado}.</p>
                  <div className="m-carga-botones">
                    <button type="button" className="m-btn m-btn-secundario" onClick={descartar}>Cancelar</button>
                    <button type="button" className="m-btn m-btn-primario" onClick={subir}>{carga.origen === 'foto' ? 'Confirmar subida' : 'Subir'}</button>
                  </div>
                </>
              )}

              {(subiendo || fase === 'verificando') && (
                <p className="m-carga-estado" role="status">
                  <i className="ti ti-loader-2 m-girando" aria-hidden="true"></i> {subiendo ? 'Subiendo…' : 'Revisando documentos…'}
                </p>
              )}

              {fase === 'error' && (
                <div className="m-carga-botones">
                  <button type="button" className="m-btn m-btn-secundario" onClick={descartar}>Cancelar</button>
                  <button type="button" className="m-btn m-btn-primario" onClick={subir}>Intentar de nuevo</button>
                </div>
              )}

              {fase === 'dudoso' && (
                <div className="m-carga-botones">
                  <button type="button" className="m-btn m-btn-primario" onClick={() => { descartar(); setAviso(null); }}>Entendido</button>
                </div>
              )}

              {fase === 'revision' && (
                <button type="button" className="m-carga-cancelar" onClick={descartar}>Cancelar</button>
              )}
            </>
          )}
        </section>
      )}

      {salidaPendiente && (
        <DialogoSalida
          subiendo={subiendo}
          textoPendiente="Tienes un documento pendiente que todavía no se ha subido. Si sales, se descartará."
          onSeguir={() => setSalidaPendiente(null)}
          onSalir={() => {
            const continuar = salidaPendiente;
            if (guardiaSalida) guardiaSalida.current = null;
            setSalidaPendiente(null);
            descartar();
            continuar();
          }}
        />
      )}
    </div>
  );
}
